import {
    CreateOptimalVariantRequestPost,
    VariantGetByIdV2,
    SystemComparisonRequest,
    SystemComparisonResponse,
    SystemComparison,
    ComparedSystem,
    SystemComparisonProduct
} from '@nx-customer-apps/api-planning-projects-v2';
import { Injectable } from '@angular/core';
import { ProductsUtils, ProductType } from '@nx-customer-apps/components';
import { roundTo, roundToStep } from '@nx-customer-apps/shared/utils';
import { ProductTile } from '@nx-customer-apps/shared/interfaces';
import {
    CalculateOptimalSystemRequest,
    CalculateOptimalSystemResponse,
    CalculateSystemComparisonResponse,
    Comparison,
    CurrentSystem,
    NewSystem
} from '../../shared/interfaces';
import { OptimizationTarget } from '../../shared/enums';
import { SystemType } from '@nx-customer-apps/shared/enums';
import {
    PRODUCT_TYPE_TO_TAG_TYPES,
    ROUNDING_STEP_TO_NEAREST_1,
    ROUNDING_STEP_TO_NEAREST_10,
    ROUNDING_STEP_TO_NEAREST_50
} from '../../shared/consts';

@Injectable({ providedIn: 'root' })
export class ResultsApiMapper {
    public mapToOptimalSystemRequestDTO = (model: CalculateOptimalSystemRequest): CreateOptimalVariantRequestPost => ({
        projectId: model.projectHexId,
        optimizationTarget: OptimizationTarget.FASTEST_PAYOFF
    });

    public mapToOptimalSystemResponseModel = (dto: VariantGetByIdV2): CalculateOptimalSystemResponse => ({
        variantId: dto.id,
        projectId: dto.projectId
    });

    public mapToSystemComparisonRequestDTO = (model: CalculateOptimalSystemResponse): SystemComparisonRequest => ({
        variantId: model.variantId,
        includeSubsidy: true
    });

    public mapToSystemComparisonResponseModel = (dto: SystemComparisonResponse): CalculateSystemComparisonResponse => ({
        comparison: this.mapToSystemComparisonModel(dto.comparison),
        newSystem: this.mapToNewSystemModel(dto.targetSystem),
        currentSystem: this.mapToCurrentSystemModel(dto.sourceSystem)
    });

    private mapToSystemComparisonModel = (dto: SystemComparison): Comparison => ({
        totalSavings: roundToStep(dto.totalSaving, ROUNDING_STEP_TO_NEAREST_50),
        paysOfAfter: roundToStep(dto.exactPayoffDurationYears, ROUNDING_STEP_TO_NEAREST_1),
        co2TotalSavings: roundToStep(dto.co2EmissionSavings, ROUNDING_STEP_TO_NEAREST_10)
    });

    private mapToNewSystemModel = (dto: ComparedSystem): NewSystem => ({
        productTiles: this.extractProductTilesFromProducts(dto.products),
        equipmentCosts: roundToStep(dto.totalEquipmentCost, ROUNDING_STEP_TO_NEAREST_50),
        subsidy: roundToStep(dto.subsidyAmount, ROUNDING_STEP_TO_NEAREST_1),
        tagTypes: this.extractTagTypesFromProducts(dto.products),
        annualRunningCosts: roundToStep(dto.annualRunningCosts, ROUNDING_STEP_TO_NEAREST_10)!,
        autarky: roundTo(dto.autarky, 1),
        co2Emissions: roundToStep(dto.co2EmissionTotal, ROUNDING_STEP_TO_NEAREST_10)!
    });

    private mapToCurrentSystemModel = (dto: ComparedSystem): CurrentSystem => ({
        tagTypes: this.extractTagTypesFromProducts(dto.products),
        annualRunningCosts: roundToStep(dto.annualRunningCosts, ROUNDING_STEP_TO_NEAREST_10)!,
        autarky: roundTo(dto.autarky, 1),
        co2Emissions: roundToStep(dto.co2EmissionTotal, ROUNDING_STEP_TO_NEAREST_10)!
    });

    private extractTagTypesFromProducts = (products: SystemComparisonProduct[]): SystemType[] => [
        ...new Set(products.flatMap(product => PRODUCT_TYPE_TO_TAG_TYPES.get(product.productType as ProductType) ?? []))
    ];

    private extractProductTilesFromProducts = (products: SystemComparisonProduct[]): ProductTile[] =>
        products.map(product => ({
            name: product.productName ?? '-',
            imageUrl: product.pictureUrl ?? ProductsUtils.placeholderImageSrc
        }));
}
